import { useState } from 'react';
import type { CompletedStep } from '../types';
import { PipelineStepRow } from './PipelineStepRow';
import { IconWrench, IconFolder, IconCheck, IconRefresh, IconStethoscope } from './icons';

interface PipelineTrailProps {
  steps: CompletedStep[];
  modifiedFiles?: string[];
}

export function PipelineTrail({ steps, modifiedFiles }: PipelineTrailProps) {
  const [expanded, setExpanded] = useState(false);

  const stepSummary = steps.reduce<Record<string, number>>((acc, s) => {
    acc[s.step] = (acc[s.step] || 0) + 1;
    return acc;
  }, {});
  const toolCalls = steps.filter(s => s.step === 'executing').length;
  const retries = steps.filter(s => s.step === 'diagnosing' && s.message?.includes('retry')).length;
  const fileCount = modifiedFiles?.length ?? 0;

  return (
    <div className="pipeline-trail" data-testid="pipeline-trail">
      <button
        className="pipeline-trail-toggle"
        onClick={() => setExpanded(!expanded)}
        data-testid="pipeline-trail-toggle"
      >
        <span className="pipeline-trail-icon">{expanded ? '▾' : '▸'}</span>
        <span className="pipeline-trail-summary">
          {toolCalls > 0 && <span className="trail-stat"><IconWrench /> {toolCalls} tool{toolCalls !== 1 ? 's' : ''}</span>}
          {fileCount > 0 && <span className="trail-stat"><IconFolder /> {fileCount} file{fileCount !== 1 ? 's' : ''}</span>}
          {stepSummary['validating'] && <span className="trail-stat"><IconCheck /> validated</span>}
          {retries > 0 && <span className="trail-stat"><IconRefresh /> {retries} retr{retries !== 1 ? 'ies' : 'y'}</span>}
          {stepSummary['diagnosing'] && !retries && <span className="trail-stat"><IconStethoscope /> diagnosed</span>}
          <span className="trail-stat trail-steps">{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
        </span>
      </button>
      {expanded && (
        <div className="pipeline-trail-details">
          {steps.map((cs, i) => (
            <PipelineStepRow key={i} cs={cs} />
          ))}
          {modifiedFiles && modifiedFiles.length > 0 && (
            <div className="pipeline-modified-files">
              {modifiedFiles.map(f => (
                <span key={f} className="pipeline-file-chip">{f}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
